// Contractor Calculation Utilities
// Phase 2 - Week 6: Contractor Management Enhancement

import { 
  Contractor, 
  ContractorPerformance, 
  ContractorStats, 
  ContractorJobAssignment,
  ContractorRating,
  ContractorMonthlyPerformance,
  CONTRACTOR_CONSTANTS 
} from '../types/contractor.ts';
import { TimesheetEntry } from '../types/timesheet.ts';
import { Invoice } from '../types/invoice.ts';
import { Employee } from '../types/employee.ts';

export class ContractorCalculator {
  
  /**
   * Calculate contractor performance metrics from job history and timesheets
   */
  static calculatePerformanceMetrics(
    contractor: Contractor,
    jobAssignments: ContractorJobAssignment[],
    timesheets: TimesheetEntry[],
    ratings: ContractorRating[]
  ): ContractorPerformance {
    const completedJobs = jobAssignments.filter(job => job.status === 'completed');
    const totalJobs = jobAssignments.length;
    
    // Job Completion Metrics
    const completionRate = totalJobs > 0 ? (completedJobs.length / totalJobs) * 100 : 0;
    const onTimeJobs = completedJobs.filter(job => 
      job.actualEndDate && job.scheduledEndDate && 
      job.actualEndDate <= job.scheduledEndDate
    );
    const onTimeCompletionRate = completedJobs.length > 0 ? 
      (onTimeJobs.length / completedJobs.length) * 100 : 0;
    
    // Quality Metrics
    const qualityRatings = ratings.map(r => r.qualityOfWork).filter(r => r > 0);
    const averageQualityScore = qualityRatings.length > 0 ? 
      qualityRatings.reduce((sum, rating) => sum + rating, 0) / qualityRatings.length : 0;
    
    const customerSatisfactionRatings = ratings.map(r => r.overallRating).filter(r => r > 0);
    const customerSatisfactionScore = customerSatisfactionRatings.length > 0 ?
      customerSatisfactionRatings.reduce((sum, rating) => sum + rating, 0) / customerSatisfactionRatings.length : 0;
    
    // Calculate rework rate (jobs that required additional work)
    const reworkJobs = completedJobs.filter(job => 
      job.updates.some(update => update.updateType === 'issue_reported')
    );
    const reworkRate = completedJobs.length > 0 ? 
      (reworkJobs.length / completedJobs.length) * 100 : 0;
    
    // Efficiency Metrics
    const jobDurations = completedJobs
      .filter(job => job.actualHours && job.actualHours > 0)
      .map(job => job.actualHours!);
    const averageJobDuration = jobDurations.length > 0 ?
      jobDurations.reduce((sum, duration) => sum + duration, 0) / jobDurations.length : 0;
    
    // Calculate productivity (jobs per week)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentJobs = completedJobs.filter(job => 
      job.completedAt && job.completedAt >= thirtyDaysAgo
    );
    const productivityScore = recentJobs.length / 4.3; // approximate weeks in 30 days
    
    // Time tracking from timesheets
    const contractorTimesheets = timesheets.filter(ts => ts.contractorId === contractor.id);
    const totalHoursWorked = contractorTimesheets.reduce((sum, ts) => sum + ts.hoursWorked, 0);
    const overtimeHours = contractorTimesheets.reduce((sum, ts) => sum + (ts.overtimeHours || 0), 0);
    const averageHoursPerJob = completedJobs.length > 0 ? totalHoursWorked / completedJobs.length : 0;
    
    // Calculate utilization rate (hours worked vs available hours)
    const availableHoursPerWeek = contractor.availability.maxHoursPerWeek;
    const weeksActive = Math.max(1, Math.ceil(totalHoursWorked / availableHoursPerWeek));
    const utilizationRate = availableHoursPerWeek > 0 ? 
      (totalHoursWorked / (availableHoursPerWeek * weeksActive)) * 100 : 0;
    
    // Financial Metrics
    const totalEarnings = completedJobs.reduce((sum, job) => sum + (job.actualCost || 0), 0);
    const averageJobValue = completedJobs.length > 0 ? totalEarnings / completedJobs.length : 0;
    const profitabilityScore = totalEarnings; // Simplified - could include costs
    
    // Recent Performance (last 30 days)
    const recentTimesheets = contractorTimesheets.filter(ts => 
      ts.date >= thirtyDaysAgo
    );
    const recentRatings = ratings.filter(r => r.createdAt >= thirtyDaysAgo);
    const recentHoursWorked = recentTimesheets.reduce((sum, ts) => sum + ts.hoursWorked, 0);
    const recentAverageRating = recentRatings.length > 0 ?
      recentRatings.reduce((sum, r) => sum + r.overallRating, 0) / recentRatings.length : 0;
    const recentOnTimeJobs = recentJobs.filter(job => 
      job.actualEndDate && job.scheduledEndDate && 
      job.actualEndDate <= job.scheduledEndDate
    );
    const recentOnTimeRate = recentJobs.length > 0 ? 
      (recentOnTimeJobs.length / recentJobs.length) * 100 : 0;
    
    // Generate monthly performance history
    const monthlyPerformance = this.calculateMonthlyPerformance(
      completedJobs, 
      contractorTimesheets, 
      ratings
    );
    
    return {
      totalJobsCompleted: completedJobs.length,
      totalJobsAssigned: totalJobs,
      completionRate,
      onTimeCompletionRate,
      averageQualityScore,
      reworkRate,
      customerSatisfactionScore,
      averageJobDuration,
      productivityScore,
      utilizationRate,
      totalEarnings,
      averageJobValue,
      profitabilityScore,
      totalHoursWorked,
      averageHoursPerJob,
      overtimeHours,
      recentMetrics: {
        jobsCompleted: recentJobs.length,
        averageRating: recentAverageRating,
        onTimeRate: recentOnTimeRate,
        hoursWorked: recentHoursWorked
      },
      monthlyPerformance
    };
  }
  
  /**
   * Calculate monthly performance history
   */
  static calculateMonthlyPerformance(
    completedJobs: ContractorJobAssignment[],
    timesheets: TimesheetEntry[],
    ratings: ContractorRating[]
  ): ContractorMonthlyPerformance[] {
    const monthlyData: { [key: string]: ContractorMonthlyPerformance } = {};
    
    // Process completed jobs
    completedJobs.forEach(job => {
      if (job.completedAt) {
        const month = job.completedAt.toISOString().substring(0, 7); // YYYY-MM
        if (!monthlyData[month]) {
          monthlyData[month] = {
            month,
            jobsCompleted: 0,
            hoursWorked: 0,
            earnings: 0,
            averageRating: 0,
            onTimeRate: 0,
            utilizationRate: 0
          };
        }
        monthlyData[month].jobsCompleted++;
        monthlyData[month].earnings += job.actualCost || 0;
      }
    });
    
    // Process timesheets
    timesheets.forEach(timesheet => {
      const month = timesheet.date.toISOString().substring(0, 7);
      if (monthlyData[month]) {
        monthlyData[month].hoursWorked += timesheet.hoursWorked;
      }
    });
    
    // Process ratings
    ratings.forEach(rating => {
      const month = rating.createdAt.toISOString().substring(0, 7);
      if (monthlyData[month]) {
        // This is simplified - in reality you'd want to average ratings properly
        monthlyData[month].averageRating = rating.overallRating;
      }
    });
    
    // Calculate on-time rates and utilization rates for each month
    Object.keys(monthlyData).forEach(month => {
      const monthJobs = completedJobs.filter(job => 
        job.completedAt && job.completedAt.toISOString().substring(0, 7) === month
      );
      const onTimeJobs = monthJobs.filter(job => 
        job.actualEndDate && job.scheduledEndDate && 
        job.actualEndDate <= job.scheduledEndDate
      );
      monthlyData[month].onTimeRate = monthJobs.length > 0 ? 
        (onTimeJobs.length / monthJobs.length) * 100 : 0;
      
      // Simplified utilization rate calculation
      monthlyData[month].utilizationRate = Math.min(100, 
        (monthlyData[month].hoursWorked / 160) * 100 // assuming 160 hours per month
      );
    });
    
    return Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));
  }
  
  /**
   * Calculate contractor statistics for dashboard
   */
  static calculateContractorStats(contractors: Contractor[]): ContractorStats {
    const total = contractors.length;
    const active = contractors.filter(c => c.status === 'active').length;
    const inactive = contractors.filter(c => c.status === 'inactive').length;
    const suspended = contractors.filter(c => c.status === 'suspended').length;
    const terminated = contractors.filter(c => c.status === 'terminated').length;
    
    // Performance aggregations
    const validRatings = contractors.filter(c => c.averageRating > 0);
    const averageRating = validRatings.length > 0 ?
      validRatings.reduce((sum, c) => sum + c.averageRating, 0) / validRatings.length : 0;
    
    const totalJobsCompleted = contractors.reduce((sum, c) => 
      sum + c.performance.totalJobsCompleted, 0);
    const totalHoursWorked = contractors.reduce((sum, c) => 
      sum + c.performance.totalHoursWorked, 0);
    const totalEarnings = contractors.reduce((sum, c) => 
      sum + c.performance.totalEarnings, 0);
    
    // Availability stats
    const available = contractors.filter(c => 
      c.availability.currentStatus === 'available').length;
    const busy = contractors.filter(c => 
      c.availability.currentStatus === 'busy').length;
    const unavailable = contractors.filter(c => 
      c.availability.currentStatus === 'unavailable').length;
    
    // Document status
    const now = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(now.getDate() + 30);
    
    let expiredDocuments = 0;
    let upcomingExpirations = 0;
    let pendingVerifications = 0;
    
    contractors.forEach(contractor => {
      contractor.documents.forEach(doc => {
        if (doc.verificationStatus === 'pending') {
          pendingVerifications++;
        }
        if (doc.expirationDate) {
          if (doc.expirationDate < now) {
            expiredDocuments++;
          } else if (doc.expirationDate <= thirtyDaysFromNow) {
            upcomingExpirations++;
          }
        }
      });
      
      // Check license and insurance expiration
      if (contractor.licenseExpires && contractor.licenseExpires < now) {
        expiredDocuments++;
      } else if (contractor.licenseExpires && contractor.licenseExpires <= thirtyDaysFromNow) {
        upcomingExpirations++;
      }
      
      if (contractor.insuranceExpires && contractor.insuranceExpires < now) {
        expiredDocuments++;
      } else if (contractor.insuranceExpires && contractor.insuranceExpires <= thirtyDaysFromNow) {
        upcomingExpirations++;
      }
    });
    
    // Recent activity
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(now.getDate() - 30);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(now.getDate() - 7);
    
    const newContractorsThisMonth = contractors.filter(c => 
      c.createdAt >= thirtyDaysAgo).length;
    const activeContractorsThisWeek = contractors.filter(c => 
      c.lastLoginAt && c.lastLoginAt >= sevenDaysAgo).length;
    const jobsCompletedThisWeek = contractors.reduce((sum, c) => 
      sum + c.performance.recentMetrics.jobsCompleted, 0);
    
    return {
      total,
      active,
      inactive,
      suspended,
      terminated,
      averageRating,
      totalJobsCompleted,
      totalHoursWorked,
      totalEarnings,
      available,
      busy,
      unavailable,
      expiredDocuments,
      upcomingExpirations,
      pendingVerifications,
      newContractorsThisMonth,
      activeContractorsThisWeek,
      jobsCompletedThisWeek
    };
  }
  
  /**
   * Calculate contractor billing from timesheets
   */
  static calculateContractorBilling(
    contractor: Contractor,
    timesheets: TimesheetEntry[],
    clientId?: string
  ): {
    regularHours: number;
    overtimeHours: number;
    holidayHours: number;
    regularPay: number;
    overtimePay: number;
    holidayPay: number;
    totalPay: number;
    hourlyRate: number;
  } {
    const contractorTimesheets = timesheets.filter(ts => ts.contractorId === contractor.id);
    
    // Get appropriate rates
    let hourlyRate = contractor.billing.defaultHourlyRate;
    let overtimeMultiplier = contractor.billing.overtimeRate;
    let holidayMultiplier = contractor.billing.holidayRate;
    
    // Use client-specific rates if available
    if (clientId && contractor.billing.clientRates[clientId]) {
      const clientRate = contractor.billing.clientRates[clientId];
      hourlyRate = clientRate.hourlyRate;
      overtimeMultiplier = clientRate.overtimeRate;
      holidayMultiplier = clientRate.holidayRate;
    }
    
    let regularHours = 0;
    let overtimeHours = 0;
    let holidayHours = 0;
    
    contractorTimesheets.forEach(timesheet => {
      switch (timesheet.workType) {
        case 'regular':
          regularHours += timesheet.hoursWorked;
          break;
        case 'overtime':
          overtimeHours += timesheet.hoursWorked;
          break;
        case 'holiday':
          holidayHours += timesheet.hoursWorked;
          break;
        case 'double_time':
          // Treat double time as overtime for contractors
          overtimeHours += timesheet.hoursWorked;
          break;
      }
    });
    
    const regularPay = regularHours * hourlyRate;
    const overtimePay = overtimeHours * hourlyRate * overtimeMultiplier;
    const holidayPay = holidayHours * hourlyRate * holidayMultiplier;
    const totalPay = regularPay + overtimePay + holidayPay;
    
    return {
      regularHours,
      overtimeHours,
      holidayHours,
      regularPay,
      overtimePay,
      holidayPay,
      totalPay,
      hourlyRate
    };
  }
  
  /**
   * Generate contractor invoice data
   */
  static generateContractorInvoice(
    contractor: Contractor,
    timesheets: TimesheetEntry[],
    startDate: Date,
    endDate: Date,
    clientId?: string
  ): {
    contractorId: string;
    contractorName: string;
    billingPeriod: { start: Date; end: Date };
    lineItems: Array<{
      description: string;
      hours: number;
      rate: number;
      amount: number;
    }>;
    subtotal: number;
    taxes: number;
    total: number;
  } {
    const periodTimesheets = timesheets.filter(ts => 
      ts.contractorId === contractor.id &&
      ts.date >= startDate &&
      ts.date <= endDate &&
      ts.status === 'approved'
    );
    
    const billing = this.calculateContractorBilling(contractor, periodTimesheets, clientId);
    
    const lineItems: Array<{
      description: string;
      hours: number;
      rate: number;
      amount: number;
    }> = [];
    
    if (billing.regularHours > 0) {
      lineItems.push({
        description: 'Regular Hours',
        hours: billing.regularHours,
        rate: billing.hourlyRate,
        amount: billing.regularPay
      });
    }
    
    if (billing.overtimeHours > 0) {
      lineItems.push({
        description: 'Overtime Hours',
        hours: billing.overtimeHours,
        rate: billing.hourlyRate * contractor.billing.overtimeRate,
        amount: billing.overtimePay
      });
    }
    
    if (billing.holidayHours > 0) {
      lineItems.push({
        description: 'Holiday Hours',
        hours: billing.holidayHours,
        rate: billing.hourlyRate * contractor.billing.holidayRate,
        amount: billing.holidayPay
      });
    }
    
    const subtotal = billing.totalPay;
    const taxes = subtotal * (contractor.billing.taxWithholding / 100);
    const total = subtotal - taxes;
    
    return {
      contractorId: contractor.id,
      contractorName: contractor.name,
      billingPeriod: { start: startDate, end: endDate },
      lineItems,
      subtotal,
      taxes,
      total
    };
  }
  
  /**
   * Calculate contractor availability score
   */
  static calculateAvailabilityScore(contractor: Contractor): number {
    const availability = contractor.availability;
    let score = 0;
    
    // Base score from current status
    switch (availability.currentStatus) {
      case 'available':
        score += 40;
        break;
      case 'busy':
        score += 20;
        break;
      case 'unavailable':
        score += 0;
        break;
      case 'on_vacation':
        score += 0;
        break;
    }
    
    // Score from capacity utilization
    const utilizationRate = availability.currentHoursScheduled / availability.maxHoursPerWeek;
    if (utilizationRate < 0.8) {
      score += 30; // Good availability
    } else if (utilizationRate < 0.95) {
      score += 15; // Limited availability
    } else {
      score += 0; // No availability
    }
    
    // Score from job capacity
    const jobUtilization = availability.currentJobCount / availability.maxJobsPerWeek;
    if (jobUtilization < 0.8) {
      score += 20;
    } else if (jobUtilization < 0.95) {
      score += 10;
    } else {
      score += 0;
    }
    
    // Score from working hours coverage
    const workingDays = Object.values(availability.workingHours).filter(day => day.available).length;
    score += (workingDays / 7) * 10;
    
    return Math.min(100, score);
  }
  
  /**
   * Find best contractors for a job based on requirements
   */
  static findBestContractorsForJob(
    contractors: Contractor[],
    jobRequirements: {
      specializations: string[];
      certifications: string[];
      location: string;
      startDate: Date;
      estimatedHours: number;
      maxTravelDistance?: number;
    }
  ): Array<{
    contractor: Contractor;
    matchScore: number;
    availabilityScore: number;
    performanceScore: number;
    totalScore: number;
  }> {
    const availableContractors = contractors.filter(c => 
      c.status === 'active' && 
      c.availability.currentStatus === 'available'
    );
    
    const scoredContractors = availableContractors.map(contractor => {
      let matchScore = 0;
      let availabilityScore = 0;
      let performanceScore = 0;
      
      // Calculate specialization match
      const contractorSpecs = contractor.specializations.map(s => s.name.toLowerCase());
      const requiredSpecs = jobRequirements.specializations.map(s => s.toLowerCase());
      const specMatches = requiredSpecs.filter(spec => 
        contractorSpecs.some(cSpec => cSpec.includes(spec))
      ).length;
      matchScore += (specMatches / requiredSpecs.length) * 30;
      
      // Calculate certification match
      const contractorCerts = contractor.certifications.map(c => c.name.toLowerCase());
      const requiredCerts = jobRequirements.certifications.map(c => c.toLowerCase());
      const certMatches = requiredCerts.filter(cert => 
        contractorCerts.some(cCert => cCert.includes(cert))
      ).length;
      matchScore += requiredCerts.length > 0 ? (certMatches / requiredCerts.length) * 20 : 20;
      
      // Calculate location match
      const isInServiceArea = contractor.availability.serviceAreas.some(area => 
        area.toLowerCase().includes(jobRequirements.location.toLowerCase())
      );
      matchScore += isInServiceArea ? 20 : 0;
      
      // Calculate availability score
      availabilityScore = this.calculateAvailabilityScore(contractor);
      
      // Calculate performance score
      const performance = contractor.performance;
      performanceScore = (
        (performance.averageQualityScore / 5) * 25 +
        (performance.onTimeCompletionRate / 100) * 25 +
        (performance.completionRate / 100) * 25 +
        (contractor.averageRating / 5) * 25
      );
      
      const totalScore = (matchScore * 0.4) + (availabilityScore * 0.3) + (performanceScore * 0.3);
      
      return {
        contractor,
        matchScore,
        availabilityScore,
        performanceScore,
        totalScore
      };
    });
    
    return scoredContractors
      .sort((a, b) => b.totalScore - a.totalScore)
      .slice(0, 10); // Return top 10 matches
  }
  
  /**
   * Format currency values
   */
  static formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }
  
  /**
   * Format percentage values
   */
  static formatPercentage(value: number, decimals: number = 1): string {
    return `${value.toFixed(decimals)}%`;
  }
  
  /**
   * Format hours
   */
  static formatHours(hours: number): string {
    return `${hours.toFixed(1)} hrs`;
  }
  
  /**
   * Validate contractor data
   */
  static validateContractorData(data: any): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!data.name || data.name.trim().length === 0) {
      errors.push('Contractor name is required');
    }
    
    if (!data.email || !this.isValidEmail(data.email)) {
      errors.push('Valid email address is required');
    }
    
    if (!data.phone || data.phone.trim().length === 0) {
      errors.push('Phone number is required');
    }
    
    if (!data.address || !data.address.street || !data.address.city || !data.address.state) {
      errors.push('Complete address is required');
    }
    
    if (!data.businessType || !['individual', 'llc', 'corporation', 'partnership'].includes(data.businessType)) {
      errors.push('Valid business type is required');
    }
    
    if (!data.billing || !data.billing.defaultHourlyRate || data.billing.defaultHourlyRate <= 0) {
      errors.push('Valid hourly rate is required');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
  
  /**
   * Validate email format
   */
  private static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

// Export utility functions for use in components
export const contractorUtils = {
  formatCurrency: ContractorCalculator.formatCurrency,
  formatPercentage: ContractorCalculator.formatPercentage,
  formatHours: ContractorCalculator.formatHours,
  calculateAvailabilityScore: ContractorCalculator.calculateAvailabilityScore,
  validateContractorData: ContractorCalculator.validateContractorData
};
